import { AxiosError, AxiosRequestConfig } from 'axios';
import { flatten, flattenDeep } from 'lodash';

import { Client } from '../Client';
import { ClientError } from '../ClientError';

type MessageType = 'Correspondence' | 'FormTask';

type Message = {
    MessageId: string;
    Subject: string;
    LastChangedDateTime: string;
    Type: MessageType;
    ServiceCode:
        | 'SKFSKB'
        | '5227'
        | '3348'
        | 'SKDPSA'
        | 'ARBHO'
        | '2933'
        | '2705'
        | '1051';
    messageRegexp?: RegExpExecArray | null;
};

interface Attachment {
    encrypted: boolean;
    fileName: string;
    fileSize: number;
    href: string;
    name: string;
    signedbydefault: boolean;
    signinglocked: boolean;
    Subject: string;
    id: string;
    messageRegexp?: RegExpExecArray | null;
    attachementRegExp?: any;
}

interface FormTask {
    href: string;
    name: string;
    signinglocked: boolean;
    signedbydefault: boolean;
}

type ErrorState =
    | 'profile-deletion-reauth'
    | 'general'
    | 'missing-requestid'
    | 'activate-error'
    | 'messages-not-found'
    | 'altinn-failed'
    | 'not-found';

export type AltinnDataType =
    | 'taxReturn'
    | 'taxRf'
    | 'taxSettlement'
    | 'shares'
    | 'companyTax'
    | 'companyAccounting'
    | 'companyShareholdres';

type EntityType = 'PERSON' | 'COMPANY';

type AltinnDataTypeObject = {
    filter: string;
    entityType: EntityType;
    notOlderThan: number;
    /**
     * A value used to indicate which document was send to /upload or /summary
     * call.
     * - /upload accepts a `fileName: {documentName}`
     * - /summary accepts `document: {documentName}`, additionally `fileName:
     * {year} {my|orgNr} {documentName}`
     */
    documentName: string;
    subjectIncludePattern: RegExp;
    subjectExcludePattern?: RegExp;
};

type DataTypes = Record<AltinnDataType, AltinnDataTypeObject>;

const dataTypes: DataTypes = {
    taxReturn: {
        filter: encodeURI(
            `?$filter=ServiceCode eq 'SKFSKB' or ServiceCode eq '5227'`,
        ),
        entityType: 'PERSON',
        notOlderThan: new Date().getFullYear() - 2,
        documentName: `Skattemelding`,
        subjectIncludePattern:
            /(skattemelding|selvangivelse|sjølvmelding|tax return|dokumentasjon|documentation)/i,
        subjectExcludePattern:
            /(foreløpig|søknad|frist|påminnelse|tilgjengelig|available)/i,
    },
    taxRf: {
        filter: encodeURI(`?$filter=ServiceCode eq '3348'`),
        entityType: 'PERSON',
        notOlderThan: new Date().getFullYear() - 2,
        documentName: `rf-1030`,
        subjectIncludePattern: /(rf-1030)/i,
    },
    taxSettlement: {
        filter: encodeURI(`?$filter=ServiceCode eq 'SKDPSA'`),
        entityType: 'PERSON',
        notOlderThan: new Date().getFullYear() - 2,
        documentName: `Skatteoppgjør`,
        subjectIncludePattern: /skatteoppgjør/i,
    },
    shares: {
        filter: encodeURI(`?$filter=ServiceCode eq 'ARBHO'`),
        entityType: 'PERSON',
        notOlderThan: new Date().getFullYear() - 2,
        documentName: `rf-1088d`,
        subjectIncludePattern: /(rf-1088d|detaljert)/i,
    },
    companyTax: {
        filter: encodeURI(`?$filter=ServiceCode eq '2933'`),
        entityType: 'COMPANY',
        notOlderThan: new Date().getFullYear() - 2,
        documentName: `rf-1028, rf-1167`,
        subjectIncludePattern: /(rf-1028|rf-1167)/i,
    },
    companyAccounting: {
        filter: encodeURI(`?$filter=ServiceCode eq '2705'`),
        entityType: 'COMPANY',
        notOlderThan: new Date().getFullYear() - 2,
        documentName: `rr-0002`,
        subjectIncludePattern: /(rr-0002)/i,
    },
    companyShareholdres: {
        filter: encodeURI(`?$filter=ServiceCode eq '1051'`),
        entityType: 'COMPANY',
        notOlderThan: new Date().getFullYear() - 2,
        documentName: `rf-1086`,
        subjectIncludePattern: /(rf-1086)/i,
    },
};

/**
 * - `OK`: Good to go!
 * - `FAILED_ACCESS`: Access denied for the requested document (most likely no
 * access to the entity as a whole)
 * - `FAILED_NOT_FOUND`: Could not find the requested document (and not due to
 * lack of access) - 404 errors on documents
 * - `FAILED_CORRUPT_FILE`: Document file contents could not be read - this
 * would most likely be a backend code to send to formue if for some reason we
 * receive a correct file
 * - `FAILED_TIMEOUT`: Timed out while fetching the document - timeout in
 * frontend for a specific document
 */
type TaxdataSummaryDocumentStatus =
    | 'OK'
    | 'FAILED_ACCESS'
    | 'FAILED_NOT_FOUND'
    | 'FAILED_CORRUPT_FILE'
    | 'FAILED_TIMEOUT';

export type AltinnAttachementsData = {
    file: Blob | null;
    fileName: string;
    status: string | TaxdataSummaryDocumentStatus;
    target: string;
    year: string | boolean;
    dataType: AltinnDataType;
    fileType: string;
} & AltinnDataTypeObject;

export type ProcessedMessage = {
    type: AltinnDataType;
    entityType: EntityType;
    target: string;
    status: 'succeed' | 'failed';
};

export class AltinnClient extends Client {
    processedMessages: ProcessedMessage[];

    taxdataSummary: {
        /**
         * This value will be replaced later on in the backend. For now we set:
         * If entityType === 'PERSON' then entityId = 'me'
         * If entityType === 'COMPANY' then entityId = orgNr (from url param)
         * of currently processed data
         */
        entityId: 'me' | string;
        entityType: EntityType;
        documents: {
            document: string;
            filename: string;
            status: TaxdataSummaryDocumentStatus;
        }[];
    }[];

    constructor(requestConfig: AxiosRequestConfig) {
        super(requestConfig);
        this.processedMessages = [];
        this.taxdataSummary = [];
    }

    /**
     * Custom error handler for Altinn requests.
     *
     * Covers case when we try to access Altinn data and the API returns us a
     * 3xx or 4xx. In those cases we do not want to break the whole process of
     * download/upload data.
     *
     * error.response:
     * The request was made and the server responded with a
     * status code that falls out of the range of 2xx
     *
     * error.request:
     * The request was made but no response was received, `error.request`
     * is an instance of XMLHttpRequest in the browser and an instance
     * of http.ClientRequest in Node.js
     *
     * else:
     * Something happened in setting up the request and triggered an Error.
     * This will be caught by parent try/catch block.
     */
    private handleAltinnAxiosRequestError = (error: AxiosError) => {
        if (error.response || error.request) {
            return [];
        } else {
            throw error;
        }
    };

    public getUrlQueryParams = () => {
        return {
            organizationNumbersParam:
                sessionStorage.getItem('organizationNumbers')?.split(',') || [],
            privateDataParam: sessionStorage.getItem('privateData')
                ? true
                : false,
            redirectUrlParam: sessionStorage.getItem('redirectUrl'),
        };
    };

    /**
     * Extract attachment's year based on its `name` and `fileName` & `Subject`.
     *
     * We need to do it using RegExp because Attachment data does not
     * contain year key.
     */
    public extractYearFromAttachment = (attachment: Attachment) => {
        const yearPattern = /\b(19|20|21)\d{2}\b/;
        const { filename } = attachment as any;

        const stringToTest = `${filename}`;

        return yearPattern.test(stringToTest)
            ? stringToTest.match(yearPattern)![0]
            : false;
    };

    /**
     * Get messages from Altinn API. Altinn supports OData filtering.
     * @see https://www.altinn.no/api/help
     */
    public getMessages = (endpoint: string): Promise<Message[]> =>
        this.client
            .get(endpoint)
            .then((response) => {
                return response.data._embedded?.messages || [];
            })
            .catch(this.handleAltinnAxiosRequestError);

    /**
     * FormTask messages returns array of `forms`. We want to retrieve form
     * with highest number.
     */
    private getFormTaskHighestId = (forms: FormTask[]) =>
        forms.sort((a, b) => {
            const getId = (href: string) => parseInt(href.split('/forms/')[1]);

            return getId(b.href) - getId(a.href);
        })[0];

    /**
     * Allows to filter FormTasks using a RegExp. The pattern is used on
     * form.name
     */
    private getFormByName = (forms: FormTask[], pattern: RegExp) =>
        forms.filter((form) => pattern.test(form.name));

    /**
     * Downloads all attachments from provided message. It's kinda misleading
     * that attachments (plural) exists in `data._links.attachment`
     * (singular) key.
     *
     * We're explictily checking if the server returned an array, otherwise
     * we return the empty [].
     *
     * Returns an empty array if the key does not exist.
     */
    public getMessageData = (
        endpoint: string,
        message: Message,
        type: AltinnDataType,
    ): Promise<Attachment[]> =>
        this.client
            .get(endpoint, { timeout: 10000 })
            .then((response) => {
                let data;
                const { Type, ServiceCode, messageRegexp } = message;
                const { subjectIncludePattern } = dataTypes[type];

                if (
                    (Type === 'FormTask' && ServiceCode === '3348') ||
                    type === 'companyShareholdres' ||
                    type === 'companyAccounting'
                ) {
                    data = response.data._links.print;
                }

                if (Type === 'FormTask' && ServiceCode === '2705') {
                    data = this.getFormByName(
                        response.data._links.form,
                        /rr-0002-u/i,
                    );
                }

                /**
                 * old: Type === 'FormTask' && ServiceCode !== '3348'
                 * data = this.getFormTaskHighestId(response.data._links.form);
                 */
                if (type === 'companyTax') {
                    const forms = response.data._links.form;
                    const filteredForms = this.filterFormTasks(forms, type);

                    data = filteredForms;
                }

                if (Type === 'Correspondence') {
                    data = response.data._links.attachment;
                }

                if (!data) {
                    return [];
                }

                if (!(data instanceof Array)) {
                    data = [data];
                }

                /**
                 * We are explicitly adding message's subject into attachements. Some
                 * of attachements does not contain name and filename.
                 */
                return data.map((d) => ({
                    ...d,
                    Subject: message.Subject,
                    id: message.MessageId,
                    messageRegexp: messageRegexp
                        ? messageRegexp[0]
                        : subjectIncludePattern,
                }));
            })
            .catch(this.handleAltinnAxiosRequestError);

    /**
     * The `year` is retrieved from attachemnt's data. It is based on its name
     * or fileName. It is used later to upload the file to UpScoreAPI.
     *
     * Attachment passed to this function should be filtered first
     * using `filterAttachments`
     *
     * Get the file datakattemelding 2019.pdf" from Altinn
     */
    public getFileData = (
        attachment: Attachment,
    ): Promise<{
        file: Blob | null;
        status: string | TaxdataSummaryDocumentStatus;
    }> => {
        const { href } = attachment;

        const callUrl = href.includes('forms') ? `${href}/print` : href;

        return this.client
            .get(callUrl, {
                baseURL: '',
                responseType: 'blob',
                headers: {
                    accept: 'application/pdf',
                },
                timeout: 180000,
            })
            .then((response) => new Blob([response.data]))
            .then((file) => ({ file, status: 'OK' }))
            .catch((error: AxiosError) => {
                console.error(error);
                let status: TaxdataSummaryDocumentStatus = 'FAILED_NOT_FOUND';

                if (error.code === 'ECONNABORTED') {
                    status = 'FAILED_TIMEOUT';
                }

                if (error.response?.status == 401) {
                    status = 'FAILED_ACCESS';
                }

                return { file: null, status };
            });
    };

    public getProfile = () =>
        this.client.get(`my/profile`).then((response) => response.data);

    // TODO: Remove duplicate code of filterFormTasks & filterMessages since are doing pretty simillar thing
    private filterFormTasks = (items: FormTask[], type: AltinnDataType) => {
        const { subjectExcludePattern, subjectIncludePattern, notOlderThan } =
            dataTypes[type];
        const yearPattern = /[^-](19|20|21)\d{2}/;

        return items
            .filter((item) => {
                /**
                 * In case there is no exclude RegExp we simply don't want to
                 * do any filtering
                 */
                if (!subjectExcludePattern) return item;
                /**
                 * Return only messages that does not match exclude pattern.
                 */
                return !subjectExcludePattern.test(item.name);
            })
            .filter((message) => {
                /**
                 * In case there is no include RegExp we simply don't want to
                 * do any filtering
                 */
                if (!subjectIncludePattern) return message;
                /**
                 * Return only messages with Subjects that match include pattern.
                 */
                return subjectIncludePattern.test(message.name);
            });
    };

    /**
     * Additional messages filter. We'd like to fetch only messages which
     * subject that match provided patterns.
     */
    private filterMessages = (messages: Message[], type: AltinnDataType) => {
        const { subjectExcludePattern, subjectIncludePattern, notOlderThan } =
            dataTypes[type];
        const yearPattern = /[^-](19|20|21)\d{2}/;

        return messages
            .filter((message) => {
                /**
                 * In case there is no exclude RegExp we simply don't want to
                 * do any filtering
                 */
                if (!subjectExcludePattern) return message;

                /**
                 * Return only messages that does not match exclude pattern.
                 */
                return !subjectExcludePattern.test(message.Subject);
            })
            .filter((message) => {
                /**
                 * In case there is no include RegExp we simply don't want to
                 * do any filtering
                 */
                if (!subjectIncludePattern) return message;

                /**
                 * Return only messages with Subjects that match include pattern.
                 */
                return subjectIncludePattern.test(message.Subject);
            })
            .filter((message) => {
                const year = parseInt(message.Subject.match(yearPattern)![0]);

                if (year >= notOlderThan) {
                    return message;
                }

                return false;
            })
            .map((message) => ({
                ...message,
                messageRegexp: subjectIncludePattern?.exec(message.Subject),
            }));
    };

    /**
     * We want to filter out messages that are older than date specified in
     * dataType[type] `notOlderThan`
     */
    private filterMessagesOlderThan = (
        messages: Message[],
        type: AltinnDataType,
    ) => {
        const yearPattern = /[^-](19|20|21)\d{2}/;
        const { notOlderThan } = dataTypes[type];

        return messages.filter((message) => {
            const year = parseInt(message.Subject.match(yearPattern)![0]);

            if (year >= notOlderThan) {
                return message;
            }

            return false;
        });
    };

    private getAltinnAttachementsData = async (
        type: AltinnDataType,
        targets: string[] = [],
    ) => {
        const { filter, documentName, entityType, subjectIncludePattern } =
            dataTypes[type];

        let errorType = 'altinn-failed' as ErrorState;

        try {
            return await Promise.all(
                targets.filter(Boolean).map(async (target) => {
                    if (!target) return;

                    const filteredMessages = await this.getMessages(
                        `${target}/messages${filter}`,
                    )
                        .then((messages) => {
                            return this.filterMessages(messages, type).sort(
                                (a, b) =>
                                    new Date(b.LastChangedDateTime).getTime() -
                                    new Date(a.LastChangedDateTime).getTime(),
                            );
                        })
                        .then((messages) => {
                            return this.filterMessagesOlderThan(messages, type);
                        });

                    const messageAttachments = await Promise.all(
                        filteredMessages.map(async (message) => {
                            return this.getMessageData(
                                `${target}/messages/${message.MessageId}`,
                                message,
                                type,
                            );
                        }),
                    ).then((attachments) => flatten(attachments));

                    this.processedMessages.push({
                        type,
                        target,
                        status: messageAttachments.length
                            ? 'succeed'
                            : 'failed',
                        entityType,
                    });

                    return await Promise.all(
                        messageAttachments.map(async (attachment) => {
                            const year =
                                this.extractYearFromAttachment(attachment);

                            const fileData = await this.getFileData(attachment);
                            const attachementRegExpGroup =
                                subjectIncludePattern.exec(attachment.name) ||
                                subjectIncludePattern.exec(attachment.Subject);
                            const attachementRegExp = attachementRegExpGroup
                                ? attachementRegExpGroup[0]
                                : documentName;

                            return {
                                dataType: type,
                                target,
                                year,
                                fileName: `${year} ${target} ${attachementRegExp} - ${attachment.id}`,
                                fileType: 'pdf',
                                ...dataTypes[type],
                                ...fileData,
                            };
                        }),
                    );
                }),
            );
        } catch (error: any) {
            if (error.message === 'Messages not found') {
                errorType = 'messages-not-found';
            }

            console.error(error);

            throw new ClientError('getAltinnData failed', {
                error,
                category: 'Altinn API',
                type: errorType,
                extras: {
                    // userData: JSON.stringify(userData, null, 2),
                    error: JSON.stringify(error, null, 2),
                },
            });
        }
    };

    /**
     * We distinguish two different Altinn data sources.
     * - Personal aka PERSON - example request to that source is `my/messages`
     * - Company aka COMPANY - example request to that source is
     * `{companyNumber}/messages`.
     *
     * There might be more than one company that we need to request
     */
    private altinnDataTargets = (entityType: EntityType) => {
        const { organizationNumbersParam } = this.getUrlQueryParams();

        return entityType === 'PERSON' ? ['my'] : organizationNumbersParam;
    };

    // TODO: Check the undefined type
    public getData = async (
        types: AltinnDataType[] = ['taxReturn'],
    ): Promise<AltinnAttachementsData | any> => {
        const attachementsData = await Promise.all(
            types.map((type) => {
                const { entityType } = dataTypes[type];

                return this.getAltinnAttachementsData(
                    type,
                    this.altinnDataTargets(entityType),
                );
            }),
        );

        return flattenDeep(attachementsData);
    };
}
